<template>
  <div class="flex-child">
    <div class="vue-chart">
      <el-skeleton :loading="status === AsyncDataStatus.PENDING" animated>
        <template #template>
          <el-skeleton-item style="height: 315px" />
        </template>
        <template #default>
          <apexchart width="100%" height="300" type="area" :options="chartOptions" :series="seriesList" />
        </template>
      </el-skeleton>
    </div>

    <div class="flex-text">
      <p v-html="$t('dashboardDexGraph', { label: formatters.label.call(currentTokenInfo?.name) })"></p>
    </div>
  </div>
</template>
<script setup lang="ts">
import { BigNumber } from 'bignumber.js';
import type { ChartLabelsModel, ChartDataModel, ChartSeriesModel, ITokenModel } from '~/types/apiService';
import useEnvs from '~/composables/useEnvs';
import type { ChartDataFormatterProps } from '~/types/common';
import { apiUrls } from '~/utils/constants';
import apexChartLocales from '~/utils/constants/apexChartLocales';
import ApexCharts from 'apexcharts';
import { colors } from '~/utils/constants/charts';
import { AsyncDataStatus } from '~/utils/constants/asyncData';
import { Formatters } from '~/utils/formatters/formatters';

const { t, locale } = useI18n();
const { blockchain, apiUrl } = useEnvs();
const store = useMainStore();
const currentToken = computed(() => store.currentToken);
const currentContractAddress = computed(() => blockchain.contracts?.[currentToken.value]?.addresses.contract);
const chartId = 'dex-chart';

defineProps<ChartDataFormatterProps>();

watch(
  locale,
  (newLocale) => {
    if (!newLocale) return;
    ApexCharts.exec(chartId, 'setLocale', newLocale);
  },
  { immediate: true }
);

const { data: currentTokenInfo } = useNuxtData<ITokenModel>('dashboardCurrentTokenInfo');

const { data, status } = await useFetch<ChartDataModel>(apiUrls.v1.statistics.rate, {
  baseURL: apiUrl,
  watch: [currentToken],
  query: {
    from: formattedLastMonthDate(),
    until: formattedTodayDate(),
    token: currentContractAddress
  },
  immediate: true
});

const labels = computed<ChartLabelsModel>(() => {
  const categories = data.value?.dates || [];

  return {
    type: 'datetime',
    categories: data.value ? categories : [],
    tickAmount: 10,
    labels: {
      rotate: 0
    }
  };
});

const seriesList = computed(() => {
  if (!data.value || !data?.value.series) return [];

  return data.value.series.map((serie: ChartSeriesModel) => {
    return {
      data: serie.data.map((item) => BigNumber(item).decimalPlaces(5).toNumber()),
      name: t(serie.name)
    };
  });
});

const chartOptions = computed(() => ({
  theme: {
    mode: 'dark'
  },
  grid: {
    borderColor: '#34414A'
  },
  chart: {
    id: chartId,
    class: 'vuechart-example',
    background: '#00000000',
    toolbar: {
      show: false
    },
    zoom: {
      enabled: false
    },
    locales: apexChartLocales,
    defaultLocale: locale.value
  },
  title: {
    text: t('coreDexRates'),
    align: 'center',
    style: {
      fontSize: '24px'
    }
  },
  responsive: [
    {
      breakpoint: 600,
      options: {
        xaxis: {
          labels: {
            rotate: -45
          }
        }
      }
    }
  ],
  stroke: {
    curve: 'smooth'
  },
  dataLabels: {
    enabled: false
  },
  ...colors,
  xaxis: labels.value,
  yaxis: {
    forceNiceScale: true,
    labels: {
      formatter: function (val: number) {
        return Formatters.number.renderString((f) => f.formatNumber(val, 5, 10, true));
      }
    }
  },
  tooltip: {
    x: {
      format: 'dd/MM/yy'
    }
  }
}));
</script>

<style lang="scss" scoped>
.vue-chart {
  min-height: 340px;
}
</style>
